<template>
    <CampaignBanner
        :master-template-height="height"
        :master-template-width="width"
        :max-height="maxHeight"
        :max-width="maxWidth"
        :scaling-factor="scalingFactor"
        @updateInternalScalingFactor="internalScalingFactor = $event"
    >
        <template #bannerContainer>
            <img
                v-if="bannerPreviewData.masterTemplate.adType === TemplateType.PSD"
                class="psd-template"
                :src="bannerPreviewData.previewUrl"
            />

            <BannerPreview
                v-else
                :id="bannerId"
                content-clickable
                lazy-load
                :preview-values="previewValues"
                :ad-type="adType"
                :scaling-factor="internalScalingFactor"
                :playback-namespace="playbackNamespace"
                @bannerLoaded="onBannerLoaded"
            />
        </template>

        <template #bannerFooter>
            <BannerFooterName :is-dark-theme="false" :banner-name="bannerName" />
        </template>
    </CampaignBanner>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import type { BannerPreviewData } from "@/types/types";
import { BannerEvents } from "@/enums/banners";
import { TemplateType } from "@/enums/masterTemplate";

import BannerPreview from "@/components/Previews/BannerPreview.vue";
import CampaignBanner from "@/components/Campaign/CampaignBanner.vue";
import BannerFooterName from "@/components/Previews/BannerFooter/BannerFooterName.vue";

import { getBannerIdFromBannerPreviewData } from "@/utils/getBannerIframeId";

export default defineComponent({
    components: {
        CampaignBanner,
        BannerPreview,
        BannerFooterName
    },

    props: {
        bannerPreviewData: {
            type: Object as PropType<BannerPreviewData>,
            required: true
        },

        playbackNamespace: {
            type: String,
            required: true
        },

        maxWidth: {
            type: Number,
            required: true
        },

        maxHeight: {
            type: Number,
            required: true
        },

        scalingFactor: {
            type: Number,
            required: true
        }
    },

    emits: [BannerEvents.BannerLoaded],

    data() {
        return {
            internalScalingFactor: 1,
            TemplateType
        };
    },

    computed: {
        adType() {
            return this.bannerPreviewData.masterTemplate.adType;
        },

        height() {
            return this.bannerPreviewData.masterTemplate.height;
        },

        width() {
            return this.bannerPreviewData.masterTemplate.width;
        },

        previewValues() {
            return JSON.stringify(this.bannerPreviewData);
        },

        bannerId() {
            return getBannerIdFromBannerPreviewData(this.bannerPreviewData, "share");
        },

        bannerName() {
            return `${this.bannerPreviewData.reportingLabel} | ${this.bannerPreviewData.masterTemplate.width} x ${this.bannerPreviewData.masterTemplate.height} - ${this.bannerPreviewData.masterTemplate.name}`;
        }
    },

    methods: {
        onBannerLoaded() {
            this.$emit(BannerEvents.BannerLoaded);
        }
    }
});
</script>

<style scoped lang="css">
.psd-template {
    width: 100%;

    display: flex;
    align-items: center;
    justify-content: center;

    background-color: white;
}
</style>
